/**
 * Thin typed client for `GET /api/web/v2/media/session` (epic
 * 2026-10-02-cloudfront-media): where logos and avatars load from, and the
 * CloudFront cookies avatars need (set by the response itself).
 *
 * `onSessionExpired` is a no-op on purpose: the session is asked for at the
 * app root, including on `/login`, and a 401 there must not redirect. A failed
 * call just leaves the web on presigned URLs.
 */
import type { MediaSessionResponse } from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface MediaClientConfig {
  apiBase: string;
  fetchImpl?: FetchLike;
}

/** A non-2xx from `/media/session`; `status` lets the provider tell a
 *  signed-out 401 (do not retry) from a failure worth asking again about. */
export class MediaSessionError extends Error {
  constructor(readonly status: number) {
    super(`media session failed (${status})`);
    this.name = "MediaSessionError";
  }
}

export function createMediaClient(cfg: MediaClientConfig) {
  const doFetch: FetchLike =
    cfg.fetchImpl ?? makeAuthedFetch({ apiBase: cfg.apiBase, onSessionExpired: () => {} });
  return {
    async session(signal?: AbortSignal): Promise<MediaSessionResponse> {
      const url = `${cfg.apiBase}/api/web/v2/media/session`;
      const res = await doFetch(url, { method: "GET", credentials: "include", signal });
      if (!res.ok) throw new MediaSessionError(res.status);
      return (await res.json()) as MediaSessionResponse;
    },
  };
}

export type MediaClient = ReturnType<typeof createMediaClient>;

export function defaultMediaClient(): MediaClient {
  return createMediaClient({ apiBase: import.meta.env.VITE_API_URL ?? "" });
}
